import type { GameMode } from '../core/types/gameMode.types';
import { getActivityPreset } from '../catalog/activityPresets';
import { validateActivityPayload } from './pluginRunner';
import { demoEcuacionesPayload } from '../activities/sandbox/sandbox.logic';

export interface ActivityGenerateRecipeInput {
  id: string;
  title: string;
  mode: GameMode;
  presetId?: string;
  aiSystemPromptExtension?: string;
}

export interface ActivityGenerateOptions {
  recipe: ActivityGenerateRecipeInput;
  subjectTag: string;
  level: string;
  difficulty: string;
  tuning?: string;
}

function topicFromTuning(tuning: string | undefined, fallback: string): string {
  const t = tuning?.trim();
  return t && t.length > 2 ? t.slice(0, 120) : fallback;
}

function buildArenaFallback(topic: string, title: string, count: number) {
  const questions = Array.from({ length: count }, (_, i) => ({
    id: `q${i + 1}`,
    kind: 'multiple-choice' as const,
    prompt: `${topic} — pregunta ${i + 1}`,
    options: [
      `Respuesta correcta sobre ${topic}`,
      `Alternativa plausible A`,
      `Alternativa plausible B`,
      `Alternativa plausible C`,
    ],
    correctIndex: 0,
    points: 100,
    timeLimitSec: 30,
  }));
  return validateActivityPayload('arena', {
    title,
    questions,
    settings: { shuffleOptions: false, showCorrectAfter: true, defaultTimeLimitSec: 30 },
  });
}

function buildPollFallback(topic: string, title: string) {
  return validateActivityPayload('poll', {
    title,
    slides: [
      {
        id: 'slide_1',
        kind: 'multiple-choice',
        prompt: topic,
        options: ['Mucho', 'Algo', 'Poco', 'Nada / No estoy seguro'],
      },
    ],
    settings: { anonymous: true, showLiveResults: true },
  });
}

function buildOrderFallback(topic: string, title: string, recipe?: ActivityGenerateRecipeInput) {
  if (recipe?.id === 'mus-compases-takadimi' || /takadimi|compás|compas/i.test(topic)) {
    const items = ['Ta', 'Ta-di', 'Ta-ka-di-mi', 'Ta'];
    return validateActivityPayload('order', {
      title,
      prompt: 'Ordena las células rítmicas para completar el compás de 4/4.',
      items,
      correctOrder: items.map((_, i) => i),
      printLayout: 'rhythm-bar',
      meter: '4/4',
      rhythmCells: [
        { syllable: 'Ta', figure: 'quarter' },
        { syllable: 'Ta-di', figure: 'eighth-pair' },
        { syllable: 'Ta-ka-di-mi', figure: 'sixteenth-quad' },
        { syllable: 'Ta', figure: 'quarter' },
      ],
    });
  }

  const segments = topic
    .split(/[,;.\n]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 3);
  const items =
    segments.length >= 3
      ? segments.slice(0, 6)
      : [
          `Primer paso: ${topic}`,
          `Segundo paso: ${topic}`,
          `Tercer paso: ${topic}`,
          `Cuarto paso: ${topic}`,
        ];
  return validateActivityPayload('order', {
    title,
    prompt: `Ordena los pasos: ${topic}`,
    items,
    correctOrder: items.map((_, i) => i),
  });
}

function buildSandboxFallback(recipe: ActivityGenerateRecipeInput, topic: string, title: string) {
  if (recipe.presetId) {
    const preset = getActivityPreset(recipe.presetId);
    if (preset) {
      const payload = preset.payload() as Record<string, unknown>;
      return validateActivityPayload('sandbox', { ...payload, title: title || String(payload.title || recipe.title) });
    }
  }
  if (recipe.id === 'mat-balanza' || recipe.aiSystemPromptExtension?.includes('inverse-operations')) {
    const base = demoEcuacionesPayload();
    return validateActivityPayload('sandbox', { ...base, title });
  }
  return validateActivityPayload('sandbox', {
    variant: 'balanza',
    title,
    targetValue: 10,
    initialValue: 0,
    maxValue: 14,
    deckMode: false,
    availableVariables: [
      { id: 'add1', label: '+ 1', internalValue: 1, type: 'ADD' },
      { id: 'sub1', label: '− 1', internalValue: -1, type: 'ADD' },
      { id: 'add3', label: '+ 3', internalValue: 3, type: 'ADD' },
    ],
  });
}

function buildCoopFallback(recipe: ActivityGenerateRecipeInput, topic: string, title: string) {
  if (recipe.presetId) {
    const preset = getActivityPreset(recipe.presetId);
    if (preset) {
      const payload = preset.payload() as Record<string, unknown>;
      return validateActivityPayload('coop', { ...payload, title: title || String(payload.title || recipe.title) });
    }
  }
  return validateActivityPayload('coop', {
    title,
    board: {
      kind: 'crossword',
      rows: 6,
      cols: 6,
      clues: [
        {
          id: 'a1',
          direction: 'across',
          row: 0,
          col: 0,
          length: 4,
          clue: `Concepto clave: ${topic}`,
          answer: 'TEMA',
        },
        {
          id: 'd1',
          direction: 'down',
          row: 0,
          col: 0,
          length: 4,
          clue: 'Unidad de aprendizaje',
          answer: 'TEMA',
        },
      ],
    },
    settings: { turnUnit: 'participant', turnDurationSec: 45 },
  });
}

function buildPointFallback(topic: string, title: string) {
  return validateActivityPayload('point', {
    title,
    prompt: topic,
    imageUrl:
      'data:image/svg+xml,' +
      encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"><rect fill="#e2e8f0" width="400" height="300"/><line x1="200" y1="20" x2="200" y2="280" stroke="#94a3b8"/><line x1="20" y1="150" x2="380" y2="150" stroke="#94a3b8"/><text x="200" y="150" text-anchor="middle" fill="#64748b" font-family="system-ui">Plano / mapa</text></svg>',
      ),
    target: { x: 0.55, y: 0.45, radius: 0.08 },
  });
}

function buildVinculaFallback(topic: string, title: string) {
  return validateActivityPayload('vincula', {
    title,
    pairs: [
      { id: 'p1', ladoA: 'Do Mayor', ladoB: 'C' },
      { id: 'p2', ladoA: 'La menor', ladoB: 'Am' },
      { id: 'p3', ladoA: 'Do Mayor Séptima', ladoB: 'Cmaj7' },
      { id: 'p4', ladoA: `Concepto: ${topic}`, ladoB: 'Definición relacionada' },
    ],
  });
}

function buildWallFallback(topic: string, title: string) {
  return validateActivityPayload('wall', {
    title,
    prompt: topic,
    maxChars: 140,
    stances: [
      { id: 'for', label: 'A favor', color: '#22c55e' },
      { id: 'against', label: 'En contra', color: '#ef4444' },
    ],
  });
}

export function generateActivityFromRecipe(options: ActivityGenerateOptions): {
  title: string;
  mode: GameMode;
  payload: unknown;
} {
  const { recipe, tuning } = options;
  const topic = topicFromTuning(tuning, recipe.title);
  const title = recipe.title;

  let payload: unknown;
  switch (recipe.mode) {
    case 'arena':
      payload = buildArenaFallback(topic, title, options.difficulty === 'hard' ? 6 : 4);
      break;
    case 'poll':
      payload = buildPollFallback(topic, title);
      break;
    case 'order':
      payload = buildOrderFallback(topic, title, recipe);
      break;
    case 'sandbox':
      payload = buildSandboxFallback(recipe, topic, title);
      break;
    case 'coop':
      payload = buildCoopFallback(recipe, topic, title);
      break;
    case 'point':
      payload = buildPointFallback(topic, title);
      break;
    case 'wall':
      payload = buildWallFallback(topic, title);
      break;
    case 'vincula':
      payload = buildVinculaFallback(topic, title);
      break;
    case 'dictado':
      payload = validateActivityPayload('dictado', {
        title,
        studentPrompt: 'Transcribe el patrón rítmico que percibes:',
        patternSyllables: ['Ta-di', 'Ta', 'Ta-di', 'Ta'],
        patternSymbols: ['♫', '♩', '♫', '♩'],
        meter: '4/4',
      });
      break;
    case 'anatomia':
      payload = validateActivityPayload('anatomia', {
        title,
        meter: '4/4',
        items: [
          { id: 'a1', symbol: '♩', figureLabel: 'Negra (Ta)', correctBeats: 1 },
          { id: 'a2', symbol: '𝅗𝅥', figureLabel: 'Blanca (Ta-a)', correctBeats: 2 },
        ],
      });
      break;
    default:
      throw new Error(`Modo no soportado: ${recipe.mode}`);
  }

  return { title, mode: recipe.mode, payload };
}
